/**
 * Which coded WhatsApp link applies to which thread (PLT-2164). A code is
 * minted for one conversation, so a link minted for an earlier thread must
 * never survive a thread change: a tap would merge the old conversation. A
 * code also expires (24 hours), and an expired one claims nothing, so a link
 * near its expiry counts as absent and the next reply or restore mints again.
 * Shared by the widget and the hosted chat page.
 */

/** A coded link as apps/ai returns it. `expiresAt` is ISO; an older apps/ai omits it. */
export type HandoffLinkData = { url: string; expiresAt?: string | null };
export type Handoff = HandoffLinkData & { threadId: string };

/**
 * A link this close to its expiry counts as expired: the visitor may not tap at
 * once. apps/ai renews a code inside the same margin (`HANDOFF_RENEW_MARGIN_MS`
 * in `@tkana/db`), so the next mint brings a new one. Keep the two equal.
 */
export const HANDOFF_EXPIRY_MARGIN_MS = 5 * 60_000;

/**
 * Whether a coded link can still be offered. A link with no expiry, or one
 * that does not parse, is trusted: apps/ai answers a stale code as an ordinary
 * message, so trusting it costs no more than the old behavior.
 */
export function isHandoffLive(
  link: { expiresAt?: string | null } | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!link) return false;
  if (!link.expiresAt) return true;
  const expiresAt = Date.parse(link.expiresAt);
  if (Number.isNaN(expiresAt)) return true;
  return now < expiresAt - HANDOFF_EXPIRY_MARGIN_MS;
}

/** The coded URL for `threadId`, or null when there is no live one for that thread. */
export function activeHandoffUrl(
  handoff: Handoff | null,
  threadId: string | null | undefined,
  now: number = Date.now(),
): string | null {
  return handoff &&
    threadId &&
    handoff.threadId === threadId &&
    isHandoffLive(handoff, now)
    ? handoff.url
    : null;
}

/**
 * The link a WhatsApp entry opens, and whether it carries a code: the stored
 * code for the active thread, else the plain greeting. Every entry reads it
 * here, the header icon and every agent card alike. A card never uses the
 * link it arrived with: a card under a message from an earlier thread holds
 * that thread's code, and a tap on it would merge the earlier conversation
 * instead of the current one. Only a coded link moves the chat, so only a
 * tap on one shows the "Your chat continues on WhatsApp" notice. Evaluate it
 * again on tap, because a page can stay open past an expiry.
 */
export function handoffLink(input: {
  handoff: Handoff | null;
  threadId: string | null | undefined;
  plainUrl: string;
  now?: number;
}): { href: string; coded: boolean } {
  const stored = activeHandoffUrl(input.handoff, input.threadId, input.now);
  return stored
    ? { href: stored, coded: true }
    : { href: input.plainUrl, coded: false };
}

/**
 * The state after a chat reply on `threadId`. A `handoff.url` in the reply
 * belongs to that thread and wins. Otherwise a stored link for another
 * thread, or an expired one, is dropped, and the caller mints when `mint` is
 * true.
 */
export function handoffAfterReply(
  handoff: Handoff | null,
  reply: {
    threadId: string;
    responseUrl?: string | null;
    responseExpiresAt?: string | null;
    hasNumber: boolean;
  },
  now: number = Date.now(),
): { handoff: Handoff | null; mint: boolean } {
  if (reply.responseUrl) {
    return {
      handoff: {
        url: reply.responseUrl,
        threadId: reply.threadId,
        expiresAt: reply.responseExpiresAt ?? null,
      },
      mint: false,
    };
  }
  const kept =
    handoff?.threadId === reply.threadId && isHandoffLive(handoff, now)
      ? handoff
      : null;
  return { handoff: kept, mint: reply.hasNumber && kept === null };
}

/**
 * The state after a mint for `mintedFor` came back. A failed mint, or one for
 * a thread that is no longer active, changes nothing, so the plain link stays.
 */
export function handoffAfterMint(
  handoff: Handoff | null,
  minted: {
    url?: string | null;
    expiresAt?: string | null;
    mintedFor: string;
    activeThreadId: string | null;
  },
): Handoff | null {
  if (!minted.url || minted.mintedFor !== minted.activeThreadId) return handoff;
  return {
    url: minted.url,
    threadId: minted.mintedFor,
    expiresAt: minted.expiresAt ?? null,
  };
}

/** The agent's side of a thread: the widget's `from` or the chat page's `role`. */
const AGENT_SENDERS = new Set(["system", "assistant"]);

/**
 * The state after a page load restored `threadId`. Without a mint here the
 * icon carries no code until the next reply, so a restored thread mints as a
 * reply would, once the agent has answered at least once. Before that the
 * plain link stays: the first reply mints. An expired stored link mints again.
 */
export function handoffAfterRestore(
  handoff: Handoff | null,
  restored: {
    threadId: string;
    senders: ReadonlyArray<string | null | undefined>;
    hasNumber: boolean;
  },
  now: number = Date.now(),
): { handoff: Handoff | null; mint: boolean } {
  const kept =
    handoff?.threadId === restored.threadId && isHandoffLive(handoff, now)
      ? handoff
      : null;
  const agentReplied = restored.senders.some(
    (sender) => sender != null && AGENT_SENDERS.has(sender),
  );
  return {
    handoff: kept,
    mint: restored.hasNumber && agentReplied && kept === null,
  };
}

/** The prefill language the mint and the chat request send. */
export function handoffLanguage(language: string | null | undefined): "ar" | "en" {
  return language === "ar" ? "ar" : "en";
}

/**
 * Mint the code for `threadId` through the worker. Resolves to the coded URL
 * and its expiry, or null on any refusal or failure, so the caller keeps the
 * plain link.
 */
export async function requestHandoffMint(input: {
  chatUrl: string;
  widgetId: string;
  threadId: string;
  language: "ar" | "en";
}): Promise<{ url: string; expiresAt: string | null } | null> {
  try {
    const response = await fetch(`${input.chatUrl}/handoff`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-widget-id": input.widgetId },
      body: JSON.stringify({ threadId: input.threadId, language: input.language }),
    });
    if (!response.ok) return null;
    const minted = (await response.json()) as { url?: unknown; expiresAt?: unknown };
    if (typeof minted.url !== "string") return null;
    return {
      url: minted.url,
      expiresAt: typeof minted.expiresAt === "string" ? minted.expiresAt : null,
    };
  } catch {
    return null;
  }
}
